import React, { useEffect, useRef } from 'react'
import { Alert, Col, Row } from 'antd'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { useIsPostHogFeatureFlagEnabledForCompany } from '@vacationtracker/shared/hooks/use-is-postHog-featureFlag-enabled-for-company'

import './team-energy-page.less'
import ActionCenter from './components/ActionCenter'
import ForecastChart from './components/ForecastChart'
import KpiTiles from './components/KpiTiles'
import MembersTable from './components/MembersTable'
import TeamEnergyContentSkeleton from './components/TeamEnergyContentSkeleton'
import TeamEnergyHeader from './components/TeamEnergyHeader'
import TeamEnergyPageSkeleton from './components/TeamEnergyPageSkeleton'
import { useTeamEnergyPage } from './hooks/useTeamEnergyPage'
import { trackPageViewed, trackSwitchDepartment } from './services/teamEnergyTracking'
import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../store/auth-user-slice'

const TeamEnergyPage: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { enabled: teamEnergyEnabled } = useIsPostHogFeatureFlagEnabledForCompany(FeatureFlagEnum.teamEnergy, authCompany?.id, authUser.id)
  const departmentId = new URLSearchParams(location.search).get('departmentId') ?? undefined

  const { data, loading, error } = useTeamEnergyPage(departmentId)
  const hasTrackedPageViewRef = useRef(false)

  // When the URL has no departmentId, the server picks a default; reflect it
  // in the URL so refresh / share / back-button behave correctly. Only run on
  // the missing-param case - when departmentId is set (even mid-switch with
  // stale `data.selected`) we must not overwrite it.
  useEffect(() => {
    if (departmentId) {
      return
    }
    const selectedId = data?.selected?.department.id
    if (!selectedId) {
      return
    }
    const params = new URLSearchParams(location.search)
    params.set('departmentId', selectedId)
    history.replace({ pathname: location.pathname, search: params.toString() })
  }, [departmentId, data?.selected?.department.id])

  // Fire TEAM_ENERGY_PAGE_VIEWED once per visit. Department switches fire
  // their own TEAM_ENERGY_DEPARTMENT_SWITCHED event instead.
  useEffect(() => {
    const selected = data?.selected
    if (!selected || hasTrackedPageViewRef.current) {
      return
    }

    hasTrackedPageViewRef.current = true
    trackPageViewed({
      departmentId: selected.department.id,
      departmentName: selected.department.name,
    })
  }, [data?.selected])

  if (!teamEnergyEnabled) {
    return <Redirect to="/" />
  }

  if (loading && !data) {
    return <TeamEnergyPageSkeleton />
  }

  if (error) {
    return (
      <div className="main-content team-energy-page">
        <Alert type="error" showIcon message={<IntlMessages id="teamEnergy.error.title" />} description={<IntlMessages id="teamEnergy.error.description" />} />
      </div>
    )
  }

  if (!data) {
    return null
  }

  const handleSelect = (id: string) => {
    trackSwitchDepartment({
      fromDepartmentId: data.selected?.department.id,
      toDepartmentId: id,
    })
    const params = new URLSearchParams(location.search)
    params.set('departmentId', id)
    history.push({ pathname: location.pathname, search: params.toString() })
  }

  const selected = data.selected
  const selectedId = departmentId ?? selected?.department.id ?? data.defaultDepartmentId
  const hasNoDepartments = data.departments.length === 0
  const hasReportMembers = Boolean(selected && selected.members.length > 0)
  const currentActiveMemberCount = selected?.department.currentActiveMemberCount ?? 0
  // No active members today → the department is empty now, regardless of the
  // (stale) snapshot, which the scheduler can't clear once a team empties out.
  const departmentIsEmpty = Boolean(selected) && currentActiveMemberCount === 0
  // Warn only while actually showing stale figures: members still exist but
  // differ from the snapshot. The empty / pending states carry their own message.
  const showStaleWarning = Boolean(selected?.department.isStale) && hasReportMembers && currentActiveMemberCount > 0

  return (
    <div className="main-content team-energy-page">
      <TeamEnergyHeader
        snapshotAt={selected?.department.snapshotAt ?? null}
        isStale={showStaleWarning}
        departments={data.departments}
        selectedDepartmentId={selectedId}
        onSelectDepartment={handleSelect}
      />
      {loading ? (
        <TeamEnergyContentSkeleton />
      ) : hasNoDepartments ? (
        <Alert
          type="info"
          showIcon
          message={<IntlMessages id="teamEnergy.empty.noDepartments.title" />}
          description={<IntlMessages id="teamEnergy.empty.noDepartments.description" />}
        />
      ) : selected ? (
        departmentIsEmpty ? (
          <Alert type="info" showIcon message={<IntlMessages id="teamEnergy.empty.noMembers.title" />} description={<IntlMessages id="teamEnergy.empty.noMembers.description" />} />
        ) : hasReportMembers ? (
          <>
            {selected.noQualifiedPtoLeaveType && (
              <Alert
                type="warning"
                showIcon
                message={<IntlMessages id="teamEnergy.noQualifiedPto.title" />}
                description={<IntlMessages id="teamEnergy.noQualifiedPto.description" />}
                style={{ marginBottom: 16 }}
              />
            )}
            {selected.forecast && selected.forecast.length > 0 ? (
              <Row gutter={[16, 16]} align="stretch" className="te-top-row">
                <Col xs={24} xxl={12}>
                  <KpiTiles
                    energy={selected.energy}
                    needAttention={selected.needAttention}
                    avgDaysSincePto={selected.avgDaysSincePto}
                    distribution={selected.distribution}
                    compact
                  />
                </Col>
                <Col xs={24} xxl={12}>
                  <ForecastChart forecast={selected.forecast} />
                </Col>
              </Row>
            ) : (
              <KpiTiles energy={selected.energy} needAttention={selected.needAttention} avgDaysSincePto={selected.avgDaysSincePto} distribution={selected.distribution} />
            )}
            {!selected.noQualifiedPtoLeaveType && (
              <ActionCenter totalInsights={selected.actionCenter.totalInsights} criticalCount={selected.actionCenter.criticalCount} insights={selected.actionCenter.insights} />
            )}
            <MembersTable members={selected.members} departmentName={selected.department.name} />
          </>
        ) : (
          <Alert
            type="info"
            showIcon
            message={<IntlMessages id="teamEnergy.empty.pendingCalculation.title" />}
            description={<IntlMessages id="teamEnergy.empty.pendingCalculation.description" />}
          />
        )
      ) : (
        <Alert type="info" showIcon message={<IntlMessages id="teamEnergy.empty.title" />} description={<IntlMessages id="teamEnergy.empty.description" />} />
      )}
    </div>
  )
}

export default TeamEnergyPage
